import React, { useEffect, useMemo } from 'react';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { ExpandableSection } from 'src/modules/shared/components/molecules/expandable-section';
import { Markdown } from 'src/modules/shared/components/molecules/markdown';
import {
  useFunnelSectionsVisibility,
  useSectionExpanded,
} from 'src/modules/settings/hooks/funnels/sections-visibility.hooks';
import {
  type Sections,
  type StatusReference,
} from '../../../../types/funnels/statuses/status-description.types';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { Text } from 'src/modules/shared/components/atoms/text';
import { PiNotePencil, PiQuestionLight } from 'react-icons/pi';
import { classes } from 'src/modules/shared/utils/styles.util';
import {
  getSectionHeightClasses,
  createStatusDefinition,
  createStepDefinition,
} from '../../../../utils/funnels/statuses/status-description.utils';
import type { FunnelReferenceMeta } from '../../../../types/funnels/statuses/status-description.types';
import { StatusReferenceHighlighter } from './editor/reference-highlighter';
import { type ReferenceDefinition } from 'src/modules/shared/utils/reference/reference-highlighter.util';
import { AiBadge } from 'src/modules/shared/components/atoms/indicators/ai-badge';

export type StatusDescriptionSectionKey = keyof Sections;

export interface StatusDescriptionSectionProps {
  sectionKey: StatusDescriptionSectionKey;
  label: string;
  tooltipContent: React.ReactNode;
  content: string;
  hasContent: boolean;
  initiallyExpanded: boolean;
  sectionUniqueId: string;
  statusReferences?: StatusReference[];
  validStepNumbers?: number[];
  isAiLinked?: boolean;
}

const highlightStatusReferences = (
  children: React.ReactNode,
  definitions: ReferenceDefinition<FunnelReferenceMeta>[]
): React.ReactNode => (
  <StatusReferenceHighlighter definitions={definitions}>
    {children}
  </StatusReferenceHighlighter>
);

export const StatusDescriptionSection: React.FC<
  StatusDescriptionSectionProps
> = ({
  sectionKey,
  label,
  tooltipContent,
  content,
  hasContent,
  initiallyExpanded,
  sectionUniqueId,
  statusReferences,
  validStepNumbers,
  isAiLinked = true,
}) => {
  const { registerSection, updateSectionExpanded } =
    useFunnelSectionsVisibility();

  useEffect(() => {
    registerSection(sectionUniqueId, hasContent, initiallyExpanded);
  }, [registerSection, sectionUniqueId, hasContent, initiallyExpanded]);

  const expanded = useSectionExpanded(sectionUniqueId, initiallyExpanded);

  const referenceDefinitions = useMemo(() => {
    if (!statusReferences || statusReferences.length === 0) {
      return [];
    }

    const definitions: ReferenceDefinition<FunnelReferenceMeta>[] = [
      createStatusDefinition(statusReferences),
      createStepDefinition(validStepNumbers),
    ];

    return definitions;
  }, [statusReferences, validStepNumbers]);

  const enhanceChildren = useMemo(() => {
    if (!referenceDefinitions.length) {
      return undefined;
    }

    return (children: React.ReactNode) =>
      highlightStatusReferences(children, referenceDefinitions);
  }, [referenceDefinitions]);

  const contentClassName = useMemo(() => {
    return getSectionHeightClasses(sectionKey);
  }, [sectionKey]);

  const containerClassName = hasContent
    ? 'bg-white shadow-sm'
    : 'border-dashed bg-secondary-50 opacity-80';
  const isLockedSection =
    !isAiLinked && sectionKey !== 'entrada' && sectionKey !== 'objetivo';

  return (
    <div
      className={classes(
        'rounded-md',
        'border',
        'border-secondary-200',
        containerClassName
      )}
    >
      <ExpandableSection
        title={label}
        actions={
          <Tooltip content={tooltipContent} placement="top" size="sm">
            <PiQuestionLight className="h-5 w-5 cursor-help" />
          </Tooltip>
        }
        isInitiallyExpanded={initiallyExpanded}
        expanded={expanded}
        expansionControl="left-icon"
        contentClassName={contentClassName}
        isContentScrollable
        onExpandChange={(isExpanded) => {
          updateSectionExpanded(sectionUniqueId, isExpanded);
        }}
      >
        <div className="flex h-full flex-col gap-3 absolute justify-center items-center inset-0">
          {isLockedSection ? (
            <div className="flex items-center justify-center">
              <div className=" flex flex-col items-center gap-2 rounded-xl border border-secondary-200 bg-secondary-50 px-4 py-4 text-center mb-2">
                <AiBadge size="sm" appearance="soft" />
                <Text
                  size="small"
                  weight="medium"
                  className="text-secondary-700"
                >
                  {/* TODO: check when implementing i18n */}
                  Sección disponible solo para estados gestionados por IA
                </Text>
                <Text size="tiny" className="text-secondary-500">
                  {/* TODO: check when implementing i18n */}
                  Agrega un agente IA a este estado para habilitar esta sección.
                </Text>
              </div>
            </div>
          ) : hasContent ? (
            <div className="flex justify-center items-center h-full px-4 text-sm text-gray-800">
              <Markdown
                className="text-sm text-gray-800 h-full"
                enhanceChildren={enhanceChildren}
              >
                {content}
              </Markdown>
            </div>
          ) : (
            <div className="flex flex-1 items-center justify-center">
              <EmptyState
                // TODO: check when implementing i18n
                description="Añade información a este bloque."
                appearance="ghost"
                size="xs"
                icon={<PiNotePencil className="h-5 w-5" />}
                placement="horizontal"
              />
            </div>
          )}
        </div>
      </ExpandableSection>
    </div>
  );
};
